import type { Reservation } from "@/store/types";
import { parseInTz } from "@/utils/date";

/** Existing reservation duration in minutes, computed from the
 *  ``date`` + ``time`` (start) and ``endDate`` + ``endTime`` (end)
 *  that the API adapter stores on the ``Reservation`` shape. The
 *  Sidebar preserves this value across time / date edits so the
 *  operator only has to pick the new start — see ``Sidebar.tsx``'s
 *  ``existingDurationMin``.
 *
 *  Cross-midnight bookings (e.g. 22:00 → 01:00 next day) rely on
 *  ``endDate`` being the day AFTER ``date`` so ``end`` doesn't land
 *  before ``start``. Legacy rows without ``endDate`` fall back to
 *  ``date`` and add a day when the clock face already wrapped; that
 *  keeps duration positive without breaking any same-day case.
 *
 *  The 15-minute floor is a defensive clamp for corrupted rows only.
 */
export function computeExistingDurationMin(
  r: Pick<Reservation, "date" | "time" | "endDate" | "endTime">,
  locationTz?: string,
): number {
  const start = parseInTz(`${r.date} ${r.time}`, "YYYY-MM-DD h:mm A", locationTz);
  const endDate = r.endDate ?? r.date;
  let end = parseInTz(`${endDate} ${r.endTime}`, "YYYY-MM-DD h:mm A", locationTz);
  // Legacy rows without ``endDate``: if the clock face wrapped past
  // midnight, roll ``end`` forward one day. Modern rows never hit
  // this branch because ``endDate`` is already the correct day.
  if (!end.isAfter(start)) {
    end = end.add(1, "day");
  }
  return Math.max(15, end.diff(start, "minute"));
}
